import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { JobVacancyFormDialogComponent } from 'src/app/components/main/employees/rrhh/job-vacancy-form-dialog/job-vacancy-form-dialog.component';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { JobVacancy, JobVacancySerialized } from 'src/app/models/job_vacancy.model';
import { ApiService, UtilsService } from 'src/app/services';
import dayjs from 'dayjs';

@Component({
  selector: 'app-job-vacancies-list',
  templateUrl: './job-vacancies-list.component.html',
  styleUrl: './job-vacancies-list.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatPaginatorModule,
    MatTableModule,
    MatTooltipModule,
  ],
})
export class JobVacanciesListComponent extends IndexTemplateComponent implements OnInit {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api_service = inject(ApiService);
  private readonly change_detector_ref = inject(ChangeDetectorRef);
  private readonly mat_dialog = inject(MatDialog);
  private readonly destroy_ref = inject(DestroyRef);
  private readonly snack_bar = inject(MatSnackBar);
  protected readonly form_builder = inject(UntypedFormBuilder);
  protected readonly router = inject(Router);
  protected readonly activated_route = inject(ActivatedRoute);
  protected readonly utils_service = inject(UtilsService);

  dataSource = new MatTableDataSource<JobVacancySerialized>([]);

  displayedColumns: string[] = ['title', 'department_categories', 'period', 'status', 'actions'];

  readonly is_active_filter_options: { value: boolean | ''; label: string }[] = [
    { value: true, label: 'Activas' },
    { value: false, label: 'Inactivas' },
    { value: '', label: 'Todas' },
  ];

  override filterFormExtraParams: Record<string, unknown> = {
    search: [''],
    is_active: [true],
  };

  constructor() {
    super(inject(Router), inject(UntypedFormBuilder), inject(ActivatedRoute), inject(UtilsService));
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.listenSearchFormControl();
    this.listenIsActiveFormControl();
  }

  openCreateDialog(): void {
    const dialog_ref = this.mat_dialog.open(JobVacancyFormDialogComponent, {
      width: '640px',
      data: { mode: 'create' },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
          this.fetchData();
        }
      });
  }

  openEditDialog(job_vacancy: JobVacancySerialized): void {
    const dialog_ref = this.mat_dialog.open(JobVacancyFormDialogComponent, {
      width: '640px',
      data: { mode: 'edit', job_vacancy },
    });

    dialog_ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((should_refresh: boolean | undefined): void => {
        if (should_refresh) {
          this.fetchData();
        }
      });
  }

  deleteJobVacancy(job_vacancy: JobVacancySerialized): void {
    if (!confirm(`¿Eliminar la vacante "${job_vacancy.title}"?`)) {
      return;
    }

    this.api_service
      .deleteJobVacancy(job_vacancy.id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (): void => {
          const current_page = Number(this.filterForm.get('page')?.value ?? 1);
          if (this.dataSource.data.length === 1 && current_page > 1) {
            this.filterForm.patchValue({ page: current_page - 1 }, { emitEvent: false });
          }
          this.fetchData();
          this.snack_bar.open('Vacante eliminada', undefined, { duration: 3000 });
        },
        error: (err: { error?: { error?: string } }): void => {
          const message = err?.error?.error ?? 'Error al eliminar.';
          this.snack_bar.open(message, undefined, { duration: 5000 });
        },
      });
  }

  override fetchData(): void {
    const search_term = this.filterForm.get('search')?.value || '';
    const is_active = this.filterForm.get('is_active')?.value as boolean | '';

    const params: Record<string, unknown> = {
      page: this.filterForm.get('page')?.value ?? 1,
      per_page: this.filterForm.get('per_page')?.value ?? this.pageSize,
      order_by: 'title:asc',
      with: 'department_categories,job_vacancy_phases',
    };

    const scope_parts: string[] = [];

    if (is_active === true) {
      scope_parts.push('active');
    } else if (is_active === false) {
      scope_parts.push('inactive');
    }

    if (scope_parts.length > 0) {
      params.scopes = scope_parts.join(',');
    }

    if (search_term) {
      params.search = search_term;
    }

    this.api_service
      .getJobVacanciesPaged(params)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: (response: ArrayResponse<JobVacancy>): void => {
          this.dataSource.data = this.serializeJobVacancies(response.data);
          this.setMetadata(response.meta.total, response.meta.current_page - 1, response.meta.per_page);
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private listenSearchFormControl(): void {
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(500), distinctUntilChanged(), takeUntilDestroyed(this.destroy_ref))
      .subscribe((search_term: string): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }

  private listenIsActiveFormControl(): void {
    this.filterForm
      .get('is_active')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((): void => {
        this.filterForm.patchValue({ page: 1 }, { emitEvent: false });
        this.fetchData();
      });
  }

  private serializeJobVacancies(job_vacancies: JobVacancy[]): JobVacancySerialized[] {
    return job_vacancies.map(job_vacancy => {
      const is_currently_active = this.isJobVacancyCurrentlyActive(job_vacancy);

      return {
        ...job_vacancy,
        department_categories_label:
          job_vacancy.department_categories?.map(department_category => department_category.name).join(', ') || '-',
        period_label: this.buildPeriodLabel(job_vacancy),
        is_currently_active,
        status_label: is_currently_active ? 'Activa' : 'Inactiva',
      };
    });
  }

  private isJobVacancyCurrentlyActive(job_vacancy: JobVacancy): boolean {
    const now = dayjs();
    const has_started = dayjs(job_vacancy.start_date).isSameOrBefore(now);
    const has_not_ended = job_vacancy.end_date === null || dayjs(job_vacancy.end_date).isSameOrAfter(now);

    return has_started && has_not_ended;
  }

  private buildPeriodLabel(job_vacancy: JobVacancy): string {
    const start_label = dayjs(job_vacancy.start_date).format('DD/MM/YYYY');
    const end_label = job_vacancy.end_date ? dayjs(job_vacancy.end_date).format('DD/MM/YYYY') : '—';

    return `${start_label} → ${end_label}`;
  }
}
